import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CheckMarkFilled from "../../src/images/checkmark-filled.svg";
import CommonStatus from "../../src/images/common_status.svg";
import DraftStatus from "../../src/images/Draft.svg";
import WithdrawnStatus from "../../src/images/withdrawn.svg";
import ExpiredStatus from "../../src/images/expired-status.png";
import mitt from "mitt";

export const emitter = mitt();
export const getDyanamoDBInstance = (dynamo) => {
  return new dynamo.AWS.DynamoDB({ apiVersion: "2012-08-10" });
};

export const isoCountryTableName = "imc-iso-countrycode";
export const awsRegion = "ap-south-1";
export const routes = {
  signup: "/signup",

  forgotPassword: "/forgot-password",
};

export const validateUrl = (url) => {
  var pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i",
  ); // fragment locator
  return !!pattern.test(url);
};

export const htmlToString = (html) => {
  if (html) {
    return html.replace(/(<([^>]+)>)/gi, "").replace(/[[\]]/g, "");
  }
  return "";
};

export function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

Array.prototype.unique = function () {
  return Array.from(new Set(this));
};

export function checkURL(url) {
  return url.match(/\.(jpeg|jpg|gif|png|svg)$/) != null;
}

export const OutsideClickHandler = (ref, callback, when, compId, setEdited) => {
  const savedCallBack = useRef(callback);

  useEffect(() => {
    savedCallBack.current = callback;
  }, [callback]);

  function handler(e) {
    if (when && e.target.id === compId) return;
    if (ref.current && !ref.current.contains(e.target)) {
      if (e.target.name !== "undoIcon") {
        savedCallBack.current(e);
      }
    }
  }

  useEffect(() => {
    if (when) {
      document.addEventListener("click", handler);
      return () => document.removeEventListener("click", handler);
    }
  }, [when]);
};

function hasDecimal(num) {
  return !!(num % 1);
}

export const roundTo2Decimals = (value) => {
  if (value === "" || value === "-" || value === null || value === undefined) {
    return value;
  }
  if (typeof value === "string" && value.endsWith(".")) {
    return value;
  }
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return value;
  return parseFloat(parsed.toFixed(2));
};

const chopTheDecimalPlaceTo2 = (value) => {
  let x = value.toString();
  if (x.includes(".")) {
    if (x.length > x.indexOf(".") + 3) {
      return x.substring(0, x.indexOf(".") + 3);
    }
    return x;
  }
  return roundTo2Decimals(value);
};

const hasMultipleDecimalPoints = (value) =>
  (String(value).match(/\./g) || []).length > 1;

const isPartialDecimalInput = (val) => {
  if (typeof val !== "string") return false;
  if (hasMultipleDecimalPoints(val)) return false;
  if (val === "." || val === "-.") return true;
  if (!val.includes(".")) return false;
  if (val.endsWith(".")) return true;
  if (/^-?\.\d*$/.test(val)) return true;
  if (/^-?\d+\.\d*0$/.test(val)) return true;
  return false;
};

export const shouldPreserveDecimalInput = (val, blur) => {
  if (blur === "blur") return false;
  return isPartialDecimalInput(String(val));
};

const formatDecimalFieldValue = (value, name, decimalRequiredFields) => {
  if (!decimalRequiredFields.includes(name)) {
    return value;
  }
  if (isPartialDecimalInput(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? value : roundTo2Decimals(parsed);
};
export const toNumericStyleValue = (value) => {
  const num = parseFloat(value);
  return Number.isNaN(num) ? 0 : roundTo2Decimals(num);
};

/** Width in px from a border shorthand such as "solid 1px #000000". */
export const getCssBorderWidth = (border) => {
  if (!border || typeof border !== "string") return 0;
  const pxMatch = border.match(/(\d+(?:\.\d+)?)\s*px/i);
  if (!pxMatch) return 0;
  const width = Number(pxMatch[1]);
  return Number.isFinite(width) && width > 0 ? width : 0;
};

/**
 * Canvas layers draw their selection ring with outline-offset:-1px, so it lands
 * on the outermost pixel of the layer and hides a thin border for as long as the
 * layer stays selected. Offsetting the ring past the border keeps border edits
 * visible while editing them.
 */
export const getSelectionOutlineOffset = (styles) => {
  const borderWidth = getCssBorderWidth(styles?.border);
  if (borderWidth <= 0) return undefined;

  const inset = borderWidth + 1;
  const shortestSide = Math.min(Number(styles?.width), Number(styles?.height));
  const maxInset =
    Number.isFinite(shortestSide) && shortestSide > 0
      ? Math.max(1, Math.floor(shortestSide / 2))
      : inset;

  return `-${Math.min(inset, maxInset)}px`;
};

export const sanitizeDecimalFieldValue = (
  retVal,
  blur,
  decimalFields,
  fieldName,
) => {
  if (retVal === -1) return -1;

  let sanitizedValue = retVal;
  const valueStr = String(retVal);

  if (hasMultipleDecimalPoints(valueStr)) return -1;

  if (blur === "blur" && valueStr.endsWith(".")) {
    sanitizedValue = valueStr.slice(0, -1);
  }

  if (blur === "blur" && (sanitizedValue === "-" || sanitizedValue === "-0")) {
    sanitizedValue = 0;
  }

  if (sanitizedValue === "" && decimalFields.includes(fieldName)) {
    sanitizedValue = 0;
  }

  if (
    blur !== "blur" &&
    decimalFields.includes(fieldName) &&
    shouldPreserveDecimalInput(sanitizedValue, blur)
  ) {
    return chopTheDecimalPlaceTo2(String(sanitizedValue));
  }

  if (
    decimalFields.includes(fieldName) &&
    sanitizedValue !== "" &&
    !(typeof sanitizedValue === "string" && sanitizedValue.endsWith("."))
  ) {
    sanitizedValue = roundTo2Decimals(sanitizedValue);
  } else if (blur === "blur") {
    if (!!Number(sanitizedValue) || sanitizedValue === "") {
      sanitizedValue = roundTo2Decimals(sanitizedValue);
    }
  }

  return sanitizedValue;
};

export const BORDER_DECIMAL_FIELDS = [
  "borderWidth",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
];

export const TIMELINE_DECIMAL_FIELDS = [
  "width",
  "height",
  "left",
  "top",
  "translateX",
  "translateY",
  "rotation",
  "rotationX",
  "rotationY",
  "transformOriginX",
  "transformOriginY",
  "transformOriginZ",
  "scale",
];

export const normalizeTimelinePopUpOptions = (options = {}) => ({
  ...options,
  ...TIMELINE_DECIMAL_FIELDS.reduce((acc, field) => {
    if (options[field] === undefined || options[field] === null) {
      return acc;
    }
    acc[field] = roundTo2Decimals(options[field]);
    return acc;
  }, {}),
});

export const COMPONENT_STYLE_DECIMAL_FIELDS = [
  ...new Set([
    ...TIMELINE_DECIMAL_FIELDS,
    ...BORDER_DECIMAL_FIELDS,
    "fontSize",
    "letterSpacing",
    "lineHeight",
    "datafontSize",
    "thumbWidth",
    "thumbHeight",
    "arrowWidth",
    "arrowHeight",
    "trackWidth",
    "trackHeight",
    //"trackRadius"
  ]),
];

export const formatDecimalDisplayValue = (value) => {
  if (value === "" || value === "-" || value === null || value === undefined) {
    return value;
  }
  if (typeof value === "string" && isPartialDecimalInput(value)) {
    return chopTheDecimalPlaceTo2(value);
  }
  return roundTo2Decimals(value);
};

export const normalizeComponentStylesDecimals = (componentStyles = {}) => {
  if (!componentStyles || typeof componentStyles !== "object") {
    return componentStyles;
  }
  const normalized = { ...componentStyles };
  COMPONENT_STYLE_DECIMAL_FIELDS.forEach((field) => {
    if (
      normalized[field] !== undefined &&
      normalized[field] !== null &&
      normalized[field] !== ""
    ) {
      normalized[field] = roundTo2Decimals(normalized[field]);
    }
  });

  return normalized;
};

export const normalizeLayerComponent = (component) => {
  if (!component || typeof component !== "object") {
    return component;
  }
  const normalized = {
    ...component,
    componentStyles: normalizeComponentStylesDecimals(component.componentStyles),
  };
  if (Array.isArray(component.scrollbarComponents)) {
    normalized.scrollbarComponents = component.scrollbarComponents.map(
      (scrollComp) =>
        scrollComp?.componentStyles
          ? {
              ...scrollComp,
              componentStyles: normalizeComponentStylesDecimals(
                scrollComp.componentStyles,
              ),
            }
          : scrollComp,
    );
  }
  return normalized;
};

export const getValidInput = (e, name, blur) => {
  let decimalRequiredFields = [
    "left",
    "top",
    "width",
    "height",
    "fontSize",
    "letterSpacing",
    "lineHeight",
    "thumbWidth",
    "thumbHeight",
    "arrowWidth",
    "arrowHeight",
    "trackWidth",
    "trackHeight",
    //"trackRadius",
    ...BORDER_DECIMAL_FIELDS,
    ...TIMELINE_DECIMAL_FIELDS,
  ];
  const signedIntegerFields = [
    "top",
    "left",
    "rotation",
    "translateX",
    "translateY",
    "rotationX",
    "rotationY",
    "transformOriginX",
    "transformOriginY",
    "transformOriginZ",
  ];
  let maxLength3Digitfields = ["fontSize", "letterSpacing", "lineHeight"];
  let maxValue =
    name === "WidthBanner" ? 1920 : name === "HeightBanner" ? 1080 : 9999;
  if (maxLength3Digitfields.includes(e.target.name)) {
    maxValue = 999;
  }
  let minValue = -9999;
  const strToNum = (val) => {
    if (decimalRequiredFields.includes(name)) {
      const str = String(val);
      if (blur !== "blur" && isPartialDecimalInput(str)) {
        return chopTheDecimalPlaceTo2(str);
      }
      return str[str.length - 1] === "." ? str : roundTo2Decimals(Number(val));
    }
    return parseInt(val);
  };

  const isNotANumber = (val) => {
    if (decimalRequiredFields.includes(name)) {
      if (hasMultipleDecimalPoints(val)) return true;
      if (val === "." || val === "-.") return false;
      if (isPartialDecimalInput(String(val))) return false;
      if (
        typeof val === "string" &&
        val.endsWith(".") &&
        !isNaN(val.slice(0, -1))
      ) {
        return false;
      }
      return isNaN(val) ? (val[0] === "-" ? false : true) : false;
    }

    return isNaN(val) || val < 0;
  };

  let value = e.target.value;
  if (value === " ") return -1;
  if (hasMultipleDecimalPoints(value)) return -1;
  if (parseInt(value) > maxValue) {
    value = maxValue;
  } else if (parseInt(value) < minValue) {
    value = minValue;
  }

  value = value.toString();

  // Keep minus-entry flow smooth when value is currently zero
  // (e.g. typing "-" with cursor after "0" yields "0-").
  if (
    signedIntegerFields.includes(e.target.name) &&
    (value === "0-" || value === "-0")
  ) {
    value = "-";
  }

  if (signedIntegerFields.includes(e.target.name)) {
    if (!isPartialDecimalInput(value) && isNaN(value) && value != "-") {
      return -1;
    }
    if (value != "" && value[0] !== "-" && isNaN(value[0])) {
      return -1;
    }
    if (value.includes(".") && !decimalRequiredFields.includes(e.target.name)) {
      return -1;
    }
  } else if (
    !signedIntegerFields.includes(e.target.name) &&
    (isNotANumber(value) || value[0] === "-")
  ) {
    return -1;
  } // Allow negative values for left, top and rotation
  let nulVal = blur === "blur" ? 0 : "";

  return value[0] !== "-"
    ? value !== ""
      ? hasDecimal(strToNum(value))
        ? chopTheDecimalPlaceTo2(value)
        : strToNum(value)
      : nulVal
    : formatDecimalFieldValue(value, name, decimalRequiredFields);
};
export const getValidInputRHS = (e, name, blur) => {
  let decimalRequiredFields = [
    "left",
    "top",
    "width",
    "height",
    "fontSize",
    "letterSpacing",
    "lineHeight",
    "thumbWidth",
    "thumbHeight",
    "arrowWidth",
    "arrowHeight",
    "trackWidth",
    "trackHeight",
    //"trackRadius",
    ...BORDER_DECIMAL_FIELDS,
    ...TIMELINE_DECIMAL_FIELDS,
  ];
  let maxLength3Digitfields = ["fontSize", "letterSpacing", "lineHeight"];
  let maxValue =
    name === "WidthBanner" ? 1920 : name === "HeightBanner" ? 1080 : 9999;
  if (maxLength3Digitfields.includes(e.target.name)) {
    maxValue = 999;
  }
  const strToNum = (val) => {
    const str = String(val);
    if (
      decimalRequiredFields.includes(name) &&
      blur !== "blur" &&
      isPartialDecimalInput(str)
    ) {
      return chopTheDecimalPlaceTo2(str);
    }
    if (str[str.length - 1] === ".") {
      return str;
    }
    return roundTo2Decimals(Number(val));
  };
  const isNotANumber = (val) => {
    if (decimalRequiredFields.includes(name)) {
      if (hasMultipleDecimalPoints(val)) return true;
      if (val === "." || val === "-.") return false;
      if (isPartialDecimalInput(String(val))) return false;
      if (
        typeof val === "string" &&
        val.endsWith(".") &&
        !isNaN(val.slice(0, -1))
      ) {
        return false;
      }
      return isNaN(val) ? (val[0] === "-" ? false : true) : false;
    }
    return isNaN(val) || val < 0;
  };

  let value = e.target.value;
  if (value === " ") return -1;
  if (hasMultipleDecimalPoints(value)) return -1;
  if (parseInt(value) > maxValue) {
    value = maxValue;
  }

  if (isNotANumber(value)) return -1;
  let nulVal = blur === "blur" ? 0 : "";

  return value[0] !== "-"
    ? value !== ""
      ? hasDecimal(strToNum(value))
        ? chopTheDecimalPlaceTo2(value)
        : strToNum(value)
      : nulVal
    : value;
  // if the string is "-" return as it is for x and Y
};

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const inlineNameValidation = (name, assetType) => {
  let retVal = { valid: true };
  let format = /[`!@#$%^&*()+\=\[\]{};':"\\|,.<>\/?~]/;
  let testCases = [
    "emptyValue",
    "spaceTest",
    "shorterLengthTest",
    "biggerLengthTest",
    "formatTest",
  ];

  for (let i = 0; i < testCases.length; i++) {
    switch (testCases[i]) {
      case "spaceTest":
        if (/\s/.test(name) === true) {
          retVal = { valid: false, message: "Spaces are not allowed" };
        }
        break;

      case "formatTest":
        if (format.test(name) === true) {
          retVal = { valid: false, message: "Special characters not allowed" };
        }
        break;

      case "shorterLengthTest":
        if (name.length <= 2) {
          retVal = { valid: false, message: "Please use minimum 3 characters" };
        }
        break;

      case "biggerLengthTest":
        if (name.length > 250) {
          retVal = {
            valid: false,
            message: "Name exceeds maximum length",
          };
        }
        break;

      default:
        retVal = { valid: true };
    }
    if (
      retVal.message === "Spaces are not allowed" ||
      retVal.message === "Special characters not allowed"
    ) {
      break;
    }
  }
  return retVal;
};
export const nameValidation = (name, assetType) => {
  let InvalidError = { message: "", valid: true };
  let format = /[`!@#$%^&*()+\=\[\]{};':"\\|,.<>\/?~]/;
  let testCases = [
    "emptyValue",

    "startsWithSpaceTest",

    "shorterLengthTest",

    "biggerLengthTest",

    "formatTest",
  ];

  for (let i = 0; i < testCases.length; i++) {
    switch (testCases[i]) {
      case "emptyValue":
        if (name.trim() === "") {
          InvalidError.valid = false;
          InvalidError.message = `Please update ${assetType} name. !`;
        }

        break;

      case "startsWithSpaceTest":
        if (/^\s/.test(name) === true) {
          InvalidError.valid = false;

          InvalidError.message = `${assetType} name can't start with spaces. !`;
        }

        break;

      case "formatTest":
        if (format.test(name) === true) {
          InvalidError.valid = false;

          InvalidError.message = ` ${assetType} name with special characters is not allowed. !`;
        }

        break;

      case "shorterLengthTest":
        const withoutSpaces = name.replace(/\s/g, "");

        const lengthWithoutSpaces = withoutSpaces.length;

        if (lengthWithoutSpaces <= 2) {
          InvalidError.valid = false;

          InvalidError.message = `Entered ${assetType} name is short. ! It should be more than 2 characters.`;
        } // }

        break;

      case "biggerLengthTest":
        if (name.length > 250) {
          InvalidError.valid = false;

          InvalidError.message = `Entered ${assetType} name is too lengthy. ! It should not be more than 250 characters.`;
        }

        break;

      default:
        InvalidError.valid = true;
    }

    if (!InvalidError.valid) break;
  }

  if (!InvalidError.valid) {
    toast.error(<div className="notificationText">{InvalidError.message}</div>);
  }

  return InvalidError.valid;
};

export const showCreatedOntime = (createdTime) => {
  const date = new Date(createdTime);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();

  return `${day}/${month}/${year}`;
};

export function convertTimestamp(timestamp) {
  const date = new Date(timestamp);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  const originaldate = `${day}/${month}/${year}`;

  const convertedTime = `${formatNumber(date.getHours())}:${formatNumber(
    date.getMinutes(),
  )}:${formatNumber(date.getSeconds())} hrs`;
  const convertedTimestamp = `${originaldate}, ${convertedTime}`;
  return convertedTimestamp;
}
export const formatNumber = (num) => {
  return num < 10 ? `0${num}` : num;
};

export const generateHTMLMultipleSizes = async (allPayload) => {
  let htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>RSD Images PDF</title>
          <style>
              body { font-family: Arial, sans-serif; }
              .image-container { display: flex; flex-direction: column; align-items: flex-start; margin: 5px; }
              .parentContainer { border: 1px solid grey; overflow-x: auto; padding: 10px; }

              .text-container {
                max-width: 100%;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
              }

              .image-container img { max-width: 100%; }
              .image-container div { margin-top: 5px; } /* Adds space between elements */

img {padding: 5px;border:0.5px solid black; }
              </style>
      </head>
      <body>
  `;

  htmlContent += `<div class="parentContainer">`;

  allPayload.map((payload, index) => {
    if (index !== 0) {
      htmlContent += `
<hr style="border:1px solid black;background-color:black;height:-0.5px;"/>

`;
    }
    htmlContent += `
      <div>
          <div style="margin-top:10px; margin-left:10px; font-weight:400;font-size:14px;">${
            index + 1
          }. ${payload?.name}</div>
          <div style="margin-top:5px; margin-left:10px; font-weight:600;font-size:14px;">${
            payload?.size
          } pixels</div>
      </div>
  `;

    if (payload?.static) {
      htmlContent += `<h4 style="color:black; text-align:left; margin-left:10px;margin-top:10px;margin-bottom:10px;">Static</h4>`;

      htmlContent += `
      <div style="margin-left:10px; width: 1050px; overflow-x: auto;">
          <div class="image-container" style="width:${payload?.width + 10}px;">
              <img style="width:${payload?.width}px;height:${
                payload?.height
              }px" src="${
                payload?.images?.[payload?.images?.length - 1]?.image || ""
              }" alt="${payload?.images?.[payload?.images?.length - 1]?.name || ""}"/>
              <div class="text-container" style="margin-top:2px;font-size:12px;">
                  ${
                    payload?.images?.[payload?.images?.length - 1]
                      ?.description || ""
                  }
              </div>
          </div>
      </div>
      
    `;
    }

    htmlContent += payload?.static
      ? `<h4 style="color:black; text-align:left; margin-left:10px;margin-top:10px;margin-bottom:10px;">Animated</h4>`
      : "";
    htmlContent += `<div style="display:flex; flex-wrap:wrap; margin-left:10px; width: 1050px; overflow-x: auto;">`;

    if (payload?.images?.length) {
      payload.images.forEach((item, index) => {
        htmlContent += `
          <div class="image-container" style="width:${payload?.width + 10}px;">
              <div style="font-size:12px; margin-bottom:5px;"><strong>FRAME ${
                index + 1
              }</strong></div>
              <img style="width:${payload?.width}px;height:${
                payload?.height
              }px" src="${item?.image}" alt="${item?.name}" />
              <div class="text-container" style="margin-top:2px;font-size:12px;">
                  ${item?.description ? item?.description : ""}
              </div>
          </div>
      `;
      });
    } else {
      htmlContent += `<p style="margin-left: 10px; color: grey;">No images available.</p>`;
    }

    htmlContent += `</div>`;
  });

  htmlContent += `</div>`;
  htmlContent += `
      </body>
      </html>
  `;

  return htmlContent;
};

function isUlEmpty(ulHtml) {
  const div = document.createElement("div");
  div.innerHTML = ulHtml.trim();
  const ul = div.querySelector("ul");

  if (!ul) return true;

  return [...ul.children].every(
    (li) =>
      li.tagName === "LI" &&
      li.textContent.trim() === "" &&
      li.children.length === 0,
  );
}

export const generateTrackHtml = async (allPayload, statusOfDoc) => {
  let htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>RSD Images PDF</title>
          <style>
              body { font-family: Arial, sans-serif; }
              .image-container { display: flex; flex-direction: column; align-items: flex-start; margin: 5px; }
              .parentContainer {  overflow-x: auto; padding: 10px; }
              .text-container {
                max-width: 100%;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
              }
              .image-container img { max-width: 100%; }
              .image-container div { margin-top: 5px; }
              img { padding: 5px; border: 0.5px solid black; }
          </style>
      </head>
      <body>
      <div class="parentContainer">
  `;

  allPayload?.forEach((payload) => {
    htmlContent += `<div style="height:100%;margin:5px;" id="${payload.name}">`;

    // Static Images
    if (payload?.static && payload?.images?.length) {
      const latestImage = payload?.images[payload?.images?.length - 1];

      htmlContent += `<h4 style="color:black; text-align:left;margin-top:10px;margin-bottom:10px;">Static</h4>`;
      htmlContent += `
      <div style="border:0.5px solid grey;border-radius:4px; overflow-x: auto;">
      <div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>FRAME ${
        latestImage?.time + 1
      }</strong><span style="color:grey;">  |  Time: </span><strong>${
        latestImage?.time
      } sec</strong></div>
      <hr style="border:-1px solid grey;"/>
          <div class="image-container" style="width:${
            payload?.width + 10
          }px;margin-bottom:15px;margin-left:15px;">
            
              <img style="width:${payload?.width}px;height:${payload?.height}px"
                  src="${
                    statusOfDoc
                      ? latestImage?.image || ""
                      : latestImage?.newImgSrc || ""
                  }" 
                  alt="${latestImage?.name || ""}"/>
              
          </div>
          <hr style="border:-1px solid grey;"/>
                
                  <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
                  <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
                      ${latestImage?.description || ""}
                  </div>
      </div>`;
    }

    // Animated Images Section
    if (payload?.images?.length) {
      htmlContent += payload?.static
        ? `<h4 style="color:black; text-align:left; margin-top:10px;margin-bottom:10px;">Animated</h4>`
        : "";

      payload?.images?.forEach((item, index) => {
        htmlContent += `<div style=" flex-wrap:wrap; border:0.5px solid grey;border-radius:4px;margin-bottom:10px;">`;
        htmlContent += `<div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>FRAME ${
          item?.time + 1
        }</strong><span style="color:grey;">  |  Time: </span><strong>${
          item?.time
        } sec</strong></div>`;
        htmlContent += `<hr style="border:-1px solid grey;" />`;
        htmlContent += `
          <div class="image-container" style="width:${
            payload?.width + 10
          }px;margin-bottom:15px;margin-left:15px;">
              <img style="width:${payload?.width}px;height:${
                payload?.height
              }px" src="${statusOfDoc ? item?.image : item?.newImgSrc}" alt="${
                item?.name
              }" />
               
          </div>`;
        htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
            
              <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
                   <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
           
              ${item?.description || ""}
              </div>`;
        htmlContent += `</div>`; // Close Animated Images Section
      });
    } else {
      htmlContent += `<p style="margin-left: 10px; color: grey;">No images available.</p>`;
    }

    htmlContent += `</div>`; // Close each payload section
    payload?.scrollImage?.map((item) => {
      const scrollDescription = item?.description
        ? item?.description
        : "This ISI/PI section will scroll up when the other animation are finished.";
      htmlContent += `<div style="height:100%;margin:5px;" id="${payload.name}-scroll">`;
      htmlContent += `<div style=" flex-wrap:wrap; border:0.5px solid grey;border-radius:4px;margin-bottom:10px;">`;
      htmlContent += `<div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>Scroll Component</strong> </strong></div>`;
      htmlContent += `<hr style="border:-1px solid grey;" />`;
      htmlContent += `
  <div class="image-container" style="width:${
    payload?.width + 10
  }px;margin-bottom:15px;margin-left:15px;">
      <img style="width:${payload?.width}px;" src="${item?.image}" alt="${
        payload?.name
      }" />
       
  </div>`;
      htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
    
      <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
           <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
   ${scrollDescription}
      </div>`;
      htmlContent += `</div>`; // Close Animated Images Section

      htmlContent += `</div>`; // Close each payload section
    });
  });

  htmlContent += `</div>`; // Close parentContainer
  htmlContent += `
      </body>
      </html>
  `;

  return htmlContent;
};

export const generateTrackChangesHtml = async (allPayload) => {
  let htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>RSD Images PDF</title>
          <style>
              body { font-family: Arial, sans-serif; }
              .image-container { display: flex; flex-direction: column; align-items: flex-start; margin: 5px; }
              .parentContainer {  overflow-x: auto; padding: 10px; }
              .text-container {
                max-width: 100%;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
              }
              .image-container img { max-width: 100%; }
              .image-container div { margin-top: 5px; }
              img { padding: 5px; border: 0.5px solid black; }
          </style>
      </head>
      <body>
      <div class="parentContainer">
  `;

  allPayload.forEach((payload) => {
    htmlContent += `<div style="height:100%;margin:5px;" id="${payload.name}">`;

    // Static Images
    if (payload?.static && payload?.images?.length) {
      const latestImage = payload?.images[payload?.images?.length - 1];

      const listItems = latestImage?.completeTrackObject
        ? Object.entries(latestImage?.completeTrackObject)?.map(
            ([key, value]) => {
              // Split camelCase and capitalize
              const formattedKey = key
                .replace(/([A-Z])/g, " $1") // Add space before capital letters
                .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter

              return `<li>${formattedKey}: ${value}</li>`;
            },
          )
        : [];

      const ulHtml = !isUlEmpty(latestImage?.trackChanges)
        ? `<span style="">${latestImage?.trackChanges}</span>`
        : latestImage?.imgChanged
          ? `<ul style="padding-left:16px">${listItems.join("")}</ul>`
          : `<div style="margin-top:12px;margin-bottom:12px">No Changes</div>`;

      htmlContent += `<h4 style="color:black; text-align:left;margin-top:10px;margin-bottom:10px;">Static</h4>`;
      htmlContent += `
      <div style="border:0.5px solid grey;border-radius:4px; overflow-x: auto;">
      <div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>FRAME ${
        latestImage.time + 1
      }</strong><span style="color:grey;">  |  Time: </span><strong>${
        latestImage.time
      } sec</strong></div>
      <hr style="border:-1px solid grey;"/>
      ${
        latestImage?.imgChanged
          ? `<div
          style="
                    background-color: #F2F3F3;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;margin-left:15px;
                  "
        >
          Old
        </div>`
          : ``
      }   
      <div class="image-container" style="width:${
        payload?.width + 10
      }px;margin-bottom:15px;margin-left:15px;">
            
              <img style="width:${payload?.width}px;height:${payload?.height}px"
                  src="${latestImage?.image || ""}" 
                  alt="${latestImage?.name || ""}"/>
              
          </div>

          ${
            latestImage?.imgChanged
              ? `<div
          style="
                    background-color:#FFEAB4;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;margin-left:15px;"

        >
          New
        </div>`
              : ``
          }  
          ${
            latestImage?.imgChanged
              ? `<div class="image-container" style="width:${
                  payload?.width + 10
                }px;margin-left:15px;">

          <img style="width:${payload?.width}px;height:${payload?.height}px"
            src="${latestImage?.newImgSrc || ""}"
            alt="${latestImage?.name || ""}" />

        </div>`
              : ``
          }

          <hr style="border:-1px solid grey;"/>
                
                  <div class="text-container" style="margin-top:11px;margin-bottom:11px;font-size:12px;margin-left:15px;">
                  <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
                      ${latestImage?.description || ""}
                  </div>

                  <hr style="border:-1px solid grey;"/>
                
                  <div class="text-container" style="margin-top:11px;margin-bottom:11px;font-size:12px;margin-left:15px;">
                  <div style="margin-bottom:5px;color:grey;font-weight:400;;font-size:12px;">Track Changes</div>
                      ${ulHtml}
                  </div>
      </div>`;
    }

    // Animated Images Section
    if (payload?.images?.length) {
      htmlContent += payload?.static
        ? `<h4 style="color:black; text-align:left; margin-top:10px;margin-bottom:10px;">Animated</h4>`
        : "";

      payload?.images?.forEach((item, index) => {
        const listItems = item?.completeTrackObject
          ? Object.entries(item?.completeTrackObject)?.map(([key, value]) => {
              // Split camelCase and capitalize
              const formattedKey = key
                .replace(/([A-Z])/g, " $1") // Add space before capital letters
                .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter

              return `<li>${formattedKey}: ${value}</li>`;
            })
          : [];
        const ulHtml = !isUlEmpty(item?.trackChanges)
          ? `<span style="">${item?.trackChanges}</span>`
          : item?.imgChanged
            ? `<ul style="padding-left:16px">${listItems.join("")}</ul>`
            : `<div  style="margin-top:12px;margin-bottom:12px">No Changes</div>`;
        htmlContent += `<div style=" flex-wrap:wrap;border:0.5px solid grey;border-radius:4px;margin-bottom:10px;">`;
        htmlContent += `<div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>FRAME ${
          item?.time + 1
        }</strong><span style="color:grey;">  |  Time: </span><strong>${
          item?.time
        } sec</strong></div>`;
        htmlContent += `<hr style="border:-1px solid grey;" />`;
        htmlContent += item?.imgChanged
          ? `<div
                  style="
                    background-color:#FFEAB4;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-left:15px;
                  "
                >
                  Old
                </div>`
          : "";
        htmlContent += `
          <div class="image-container" style="width:${
            payload?.width + 10
          }px;margin-left:15px;margin-bottom:15px;">
              <img style="width:${payload?.width}px;height:${
                payload?.height
              }px" src="${item?.image}" alt="${item?.name}" />
               
          </div>`;

        htmlContent += item?.imgChanged
          ? `<div
                  style="
                    background-color:#FFEAB4;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-left:15px;
                  "
                >
                  New
                </div>`
          : "";

        htmlContent += item?.imgChanged
          ? `
          <div class="image-container" style="width:${
            payload?.width + 10
          }px;margin-left:15px;">
              <img style="width:${payload?.width}px;height:${
                payload?.height
              }px" src="${item?.newImgSrc}" alt="${item?.name}" />
               
          </div>`
          : "";

        htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
            
              <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
                   <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
           
              ${item?.description || ""}
              </div>`;

        htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
            
              <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
                   <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Track changes</div>
           
              ${ulHtml}
              </div>`;
        htmlContent += `</div>`; // Close Animated Images Section
      });
    } else {
      htmlContent += `<p style="margin-left: 10px; color: grey;">No images available.</p>`;
    }

    htmlContent += `</div>`; // Close each payload section
    payload?.scrollImage?.map((item) => {
      const listItems = item?.completeTrackObject
        ? Object.entries(item?.completeTrackObject)?.map(([key, value]) => {
            // Split camelCase and capitalize
            const formattedKey = key
              .replace(/([A-Z])/g, " $1") // Add space before capital letters
              .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter

            return `<li>${formattedKey}: ${value}</li>`;
          })
        : [];
      const ulHtml = !isUlEmpty(item?.trackChanges)
        ? `<span style="">${item?.trackChanges}</span>`
        : item?.imgChanged
          ? `<ul style="padding-left:16px">${listItems.join("")}</ul>`
          : `<div  style="margin-top:12px;margin-bottom:12px">No Changes</div>`;
      const scrollDescription = item?.description
        ? item.description
        : "This ISI/PI section will scroll up when the other animation are finished.";
      htmlContent += `<div style="height:100%;margin:5px;" id="${payload.name}-scroll">`;
      htmlContent += `<div style=" flex-wrap:wrap; border:0.5px solid grey;border-radius:4px;margin-bottom:10px;">`;
      htmlContent += `<div style="font-size:14px; margin-top:15px;margin-bottom:15px;margin-left:15px;"><strong>Scroll Component</strong> </strong></div>`;
      htmlContent += `<hr style="border:-1px solid grey;" />`;
      htmlContent += item?.imgChanged
        ? `<div
                  style="
                    background-color:#FFEAB4;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-left:15px;
                  "
                >
                  Old
                </div>`
        : "";

      htmlContent += `
  <div class="image-container" style="width:${
    payload.width + 10
  }px;margin-bottom:15px;margin-left:15px;">
      <img style="width:${payload.width}px;" src="${item.image}" alt="${
        payload.name
      }" />
       
  </div>`;
      htmlContent += item?.imgChanged
        ? `<div
                  style="
                    background-color:#FFEAB4;
                    height: 24px;
                    width: 50px;
                    font-weight: 600;
                    font-size: 14px;
                    border-radius: 4px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-left:15px;
                  "
                >
                  New
                </div>`
        : "";

      htmlContent += item?.imgChanged
        ? `
          <div class="image-container" style="width:${
            payload.width + 10
          }px;margin-left:15px;">
              <img style="width:${payload.width}px;" src="${
                item.newImgSrc
              }" alt="${item.name}" />
               
          </div>`
        : "";

      htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
    
      <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
           <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Animation Notes</div>
   ${scrollDescription}
     
      </div>`;
      htmlContent += `<hr style="border:-1px solid grey;width:100%"/>
            
              <div class="text-container" style="margin-top:15px;margin-bottom:15px;font-size:12px;margin-left:15px;">
                   <div style="margin-bottom:5px;color:grey;font-weight:400;font-size:12px;">Track changes</div>
           
              ${ulHtml}
              </div>`;

      htmlContent += `</div>`; // Close Animated Images Section

      htmlContent += `</div>`; // Close each payload section
    });
  });

  htmlContent += `</div>`; // Close parentContainer
  htmlContent += `
      </body>
      </html>
  `;

  return htmlContent;
};

export const generateHTML = async (payload) => {
  let htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>RSD Images PDF</title>
          <style>
              body { font-family: Arial, sans-serif; }
              .image-container { display: flex; flex-direction: column; align-items: flex-start; margin: 5px; }
              .parentContainer { border: 1px solid grey; overflow-x: auto; padding: 10px; }

              .text-container {
                max-width: 100%;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                word-break: break-word;
              }

              .image-container img { max-width: 100%; }
              .image-container div { margin-top: 5px; } /* Adds space between elements */

              img { width: ${payload.width}px; height: ${payload.height}px; border: 0.5px solid black; padding: 5px; }
          </style>
      </head>
      <body>
  `;

  htmlContent += `<div class="parentContainer">`;

  htmlContent += `
      <div>
          <div style="margin-top:10px; margin-left:10px; font-weight:600;">${payload.name}</div>
          <div style="margin-top:5px; margin-left:10px; font-weight:600;">${payload.size} pixels</div>
      </div>
  `;

  htmlContent += `<hr/>`;

  if (payload?.static) {
    htmlContent += `<h4 style="color:red; text-align:left; margin-left:10px;margin-top:10px;margin-bottom:10px;">Static</h4>`;

    htmlContent += `
      <div style="margin-left:10px; width: 1050px; overflow-x: auto;">
          <div class="image-container" style="width:${payload.width + 10}px;">
              <img src="${
                payload?.images?.[payload.images.length - 1]?.image || ""
              }" alt="${
                payload?.images?.[payload.images.length - 1]?.name || ""
              }"/>
              <div class="text-container" style="margin-top:5px;">
                  ${
                    payload?.images?.[payload.images.length - 1]?.description ||
                    ""
                  }
              </div>
          </div>
      </div>
      <hr/>
    `;
  }

  htmlContent += `<h4 style="color:red; text-align:left; margin-left:10px;">Animated</h4>`;
  htmlContent += `<div style="display:flex; flex-wrap:wrap; margin-left:10px; width: 1050px; overflow-x: auto;">`;

  if (payload?.images?.length) {
    payload.images.forEach((item, index) => {
      htmlContent += `
          <div class="image-container" style="width:${payload.width + 10}px;">
              <div style="font-size:12px; margin-bottom:5px;"><strong>FRAME ${
                index + 1
              }</strong></div>
              <img src="${item.image}" alt="${item.name}" />
              <div class="text-container" style="margin-top:5px;">
                  ${item.description ? item.description : ""}
              </div>
          </div>
      `;
    });
  } else {
    htmlContent += `<p style="margin-left: 10px; color: grey;">No images available.</p>`;
  }

  htmlContent += `</div>`;
  htmlContent += `</div>`;
  htmlContent += `
      </body>
      </html>
  `;

  return htmlContent;
};

export const downloadFile = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      mode: "cors",
    });

    if (!response.ok) throw new Error("Failed to fetch file");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = "file.pdf";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed", error);
  }
};

export const NO_PREVIEW_BASE64 =
  "data:image/png;base64,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";

export const getStatusIcon = (status) => {
  if (!status) return CommonStatus;
  switch (status.toLowerCase()) {
    case "not submitted":
    case "draft":
      return DraftStatus;
    case "approved for use":
      return CheckMarkFilled;
    case "expired":
      return ExpiredStatus;
    case "pending withdrawal":
    case "withdrawn":
      return WithdrawnStatus;
    default:
      return CommonStatus;
  }
};

export const costomEllipsis = (str, maxLength) => {
  if (str?.length > maxLength) {
    return str.slice(0, maxLength - 3) + "...";
  }
  return str;
};

export const MAX_FILE_SIZE = 500 * 1024;
export const MAX_FILE_SIZE_LABEL = "500 KB";
export const UPLOAD_PROGRESS_STEPS = [20, 40, 60, 80];
export const UPLOAD_PROGRESS_INTERVAL_MS = 500;
export const UPLOAD_PROGRESS_FINAL_STEP_DELAY_MS = 200;
export const UPLOAD_COMPLETE_HOLD_MS = 500;
export const INVALID_FILE_FORMAT_TEXT =
  "Invalid file format. Please upload a JPG or PNG image only.";
export const PAGE_LOAD_ERROR_MESSAGE =
  "Unable to load backup image settings. Please check your connection and retry.";

const SUPPORTED_EXTENSIONS = /\.(jpg|jpeg|png)$/i;
const SUPPORTED_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const isJpegSignature = (bytes) =>
  bytes.length >= 3 &&
  bytes[0] === 0xff &&
  bytes[1] === 0xd8 &&
  bytes[2] === 0xff;

const isPngSignature = (bytes) =>
  bytes.length >= 8 &&
  bytes[0] === 0x89 &&
  bytes[1] === 0x50 &&
  bytes[2] === 0x4e &&
  bytes[3] === 0x47 &&
  bytes[4] === 0x0d &&
  bytes[5] === 0x0a &&
  bytes[6] === 0x1a &&
  bytes[7] === 0x0a;

export const isSupportedImageContent = async (file) => {
  try {
    const headerBuffer = await file.slice(0, 8).arrayBuffer();
    const headerBytes = new Uint8Array(headerBuffer);
    return isJpegSignature(headerBytes) || isPngSignature(headerBytes);
  } catch (error) {
    return false;
  }
};

export const isSupportedFileType = (file) =>
  SUPPORTED_TYPES.includes(file?.type) ||
  SUPPORTED_EXTENSIONS.test(file?.name || "");

export const normalizeSizeLabel = (label) => {
  if (!label && label !== 0) return "";
  const textValue = String(label).toUpperCase();
  const parts = textValue.match(/\d+/g);
  if (parts && parts.length >= 2) {
    return `${parts[0]}X${parts[1]}`;
  }
  return textValue.replace(/\s+/g, "");
};

export const formatSizeFromApi = (sizeValue) => {
  const normalized = normalizeSizeLabel(sizeValue);
  const parts = normalized.split("X").map((value) => Number(value));
  if (parts.length === 2 && parts.every((value) => Number.isFinite(value))) {
    return `${parts[1]}X${parts[0]}`;
  }
  return normalized;
};

export const parseSizeOptionsFromStorage = () => {
  try {
    const rawData = JSON.parse(
      localStorage.getItem("allBannersForVeevaId") || "[]",
    );
    if (!Array.isArray(rawData)) return [];
    return rawData
      .filter((item) => item?.value)
      .map((item) => ({
        label: normalizeSizeLabel(item?.label),
        value: item?.value,
      }));
  } catch (error) {
    return [];
  }
};

export const hasTrailingSizeLabel = (assetName, sizeLabel) => {
  const normalizedAssetName = String(assetName || "").trim();
  const normalizedSizeLabel = normalizeSizeLabel(sizeLabel);
  if (!normalizedAssetName || !normalizedSizeLabel) return false;
  const trailingSizeMatch = normalizedAssetName.match(/(\d+\s*[xX]\s*\d+)\s*$/);
  if (!trailingSizeMatch?.[1]) return false;
  return normalizeSizeLabel(trailingSizeMatch[1]) === normalizedSizeLabel;
};

export const formatFileSizeKb = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  const sizeInKb = bytes / 1024;
  if (sizeInKb >= 100) return `${Math.round(sizeInKb)} KB`;
  if (sizeInKb < 1) return "1 KB";
  return `${sizeInKb.toFixed(1)} KB`;
};

export const truncateFileName = (fileName) => {
  const name = `${fileName || ""}`;
  if (name.length <= 24) return name;
  return `${name.slice(0, 9)}...${name.slice(-9)}`;
};

export const getOriginalFilename = (fileName = "") => {
  const extensionIndex = fileName.lastIndexOf(".");

  if (extensionIndex === -1) return fileName;

  const extension = fileName.slice(extensionIndex);
  const baseName = fileName.slice(0, extensionIndex);

  const cleanedBaseName = baseName.replace(
    /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "",
  );

  return `${cleanedBaseName}${extension}`;
};

export const deriveFileNameFromUrl = (url) => {
  const value = String(url || "").trim();
  if (!value) return "";
  try {
    const urlPath = new URL(value).pathname;
    const fileName = urlPath.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(fileName);
  } catch (error) {
    const fileName = value.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(fileName);
  }
};

export const extractBackupImageData = (backupResponse) => {
  const backupData = backupResponse?.data || {};
  const backupImageData = backupData?.data || backupData;
  const imageUrl = backupImageData?.imageUrl || backupImageData?.s3Url || "";
  const fileName =
    backupImageData?.fileName || deriveFileNameFromUrl(imageUrl) || "";
  const rawFileSize =
    backupImageData?.fileSize ||
    backupImageData?.size ||
    backupImageData?.fileSizeInBytes;
  const fileSize = Number(rawFileSize);
  return {
    imageUrl,
    fileName,
    fileSize: Number.isFinite(fileSize) && fileSize > 0 ? fileSize : 0,
  };
};
